"use client";

import { useState } from "react";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@tracify/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@tracify/ui/collapsible";
import type { ChatMessage } from "@tracify/shared";
import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "lucide-react";
import {
  getToolDisplayName,
  ToolCallFallback,
} from "@/components/ai-elements/tool-call";
import { ToolCallOutput } from "./tool-call-output";
import type {
  PendingToolCallApproval,
  ToolCallApprovalActionInput,
} from "../tool-call-approval";

type ToolPart = Extract<
  ChatMessage["parts"][number],
  { type: `tool-${string}` }
>;

function getVisibility(
  part: ToolPart,
  approvalData?: PendingToolCallApproval | null
) {
  const showOutput = part.state === "output-available";
  const showError =
    part.state === "output-error" || part.state === "output-denied";
  const showInput =
    part.state === "input-streaming" ||
    part.state === "input-available" ||
    part.state === "approval-requested" ||
    part.state === "approval-responded" ||
    part.state === "output-available" ||
    part.state === "output-error" ||
    part.state === "output-denied" ||
    approvalData != null;

  return { showInput, showOutput, showError };
}

export function ToolCallDetails({
  part,
  className,
  approvalData,
  isToolApprovalPending = false,
  onApproveToolCall,
  onDenyToolCall,
}: {
  part: ToolPart;
  className?: string;
  approvalData?: PendingToolCallApproval | null;
  isToolApprovalPending?: boolean;
  onApproveToolCall?: (input: ToolCallApprovalActionInput) => void;
  onDenyToolCall?: (input: ToolCallApprovalActionInput) => void;
}) {
  const { showInput, showOutput, showError } = getVisibility(
    part,
    approvalData
  );
  const inputValue = approvalData?.args ?? part.input;
  const showApprovalActions =
    part.state === "approval-requested" && approvalData != null;

  if (!showInput && !showOutput && !showError) {
    return null;
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {showInput && (
        <div className="flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Input
          </div>
          {part.state === "input-streaming" && inputValue === undefined ? (
            <div className="text-xs text-muted-foreground">
              Streaming tool input…
            </div>
          ) : (
            <ToolCallFallback value={inputValue} />
          )}
        </div>
      )}

      {showApprovalActions ? (
        <div className="flex flex-wrap gap-2">
          {isToolApprovalPending ? (
            <div className="text-xs text-muted-foreground">Processing…</div>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                disabled={!onApproveToolCall}
                onClick={() =>
                  onApproveToolCall?.({
                    approvalId: approvalData!.approvalId,
                  })
                }
              >
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!onDenyToolCall}
                onClick={() =>
                  onDenyToolCall?.({
                    approvalId: approvalData!.approvalId,
                  })
                }
              >
                Deny
              </Button>
            </>
          )}
        </div>
      ) : null}

      {showOutput && (
        <div className="flex flex-col gap-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Output
          </div>
          <ToolCallOutput part={part} />
          <ToolCallFallback value={part.output} />
        </div>
      )}

      {showError && (
        <div className="flex flex-col gap-1">
          <div className="text-[11px] uppercase tracking-wide text-destructive">
            Error
          </div>
          <div className="text-xs rounded-md border border-destructive/30 bg-destructive/10 py-2 text-destructive">
            {part.state === "output-denied"
              ? (part.approval?.reason ?? "Tool execution denied.")
              : part.errorText}
          </div>
        </div>
      )}
    </div>
  );
}

export function ToolCallCard({
  part,
  title,
  isActive = false,
  tone = "default",
  className,
  approvalData,
  isToolApprovalPending = false,
  onApproveToolCall,
  onDenyToolCall,
}: {
  part: ToolPart;
  title?: string;
  isActive?: boolean;
  tone?: "default" | "muted";
  className?: string;
  approvalData?: PendingToolCallApproval | null;
  isToolApprovalPending?: boolean;
  onApproveToolCall?: (input: ToolCallApprovalActionInput) => void;
  onDenyToolCall?: (input: ToolCallApprovalActionInput) => void;
}) {
  const toolName = title ?? getToolDisplayName(part.type);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible
      className={cn("flex flex-col gap-2", className)}
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          aria-busy={isActive || undefined}
          className={cn(
            "group inline-flex max-w-full items-start justify-start gap-2 self-start text-left text-sm text-foreground/70 transition-colors",
            tone === "muted"
              ? "font-medium hover:text-foreground"
              : "hover:text-foreground"
          )}
        >
          <span className="min-w-0">
            <span className="font-medium">
              {isActive ? <Shimmer>{toolName}</Shimmer> : toolName}
            </span>
          </span>
          <ChevronDownIcon
            className={cn(
              "mt-0.5 size-4 shrink-0 opacity-0 transition-[opacity,transform] group-hover:opacity-100",
              isOpen && "rotate-180"
            )}
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2">
        <ToolCallDetails
          part={part}
          approvalData={approvalData}
          isToolApprovalPending={isToolApprovalPending}
          onApproveToolCall={onApproveToolCall}
          onDenyToolCall={onDenyToolCall}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
